import {
	Bell,
	Check,
	CheckCircle2,
	ChevronDown,
	ChevronUp,
	Globe,
	Loader2,
	Mail,
	Plus,
	Send,
	Shield,
	Trash2,
	X,
	XCircle,
} from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import client from "../../api/client";
import {
	Button,
	Input,
	Notification,
	TextArea,
	useNotification,
} from "../../components/FormComponents";
import { PERMISSIONS_MATRIX, ROLES } from "../../constants/roles";

export default function Settings({ onSettingsSaved }) {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [testingWebhook, setTestingWebhook] = useState(false);
	const [testingEmail, setTestingEmail] = useState(false);
	const [rejectionReasons, setRejectionReasons] = useState([]);
	const [newReasonLabel, setNewReasonLabel] = useState("");
	const [reasonsLoading, setReasonsLoading] = useState(false);
	const { notification, showNotification } = useNotification();
	const [settings, setSettings] = useState({
		app_name: "",
		app_logo_url: "",
		webhook_url: "",
		smtp_host: "",
		smtp_port: 587,
		smtp_user: "",
		smtp_pass: "",
		smtp_from: "",
		email_enabled: false,
		notify_on_create: false,
		notify_on_accept: false,
		notify_on_reject: false,
		notify_on_expiring: false,
		admin_notification_email: "",
	});

	const fetchSettings = useCallback(async () => {
		try {
			setLoading(true);
			const response = await client.get("/settings");
			setSettings({
				app_name: response.data.app_name || "",
				app_logo_url: response.data.app_logo_url || "",
				webhook_url: response.data.webhook_url || "",
				smtp_host: response.data.smtp_host || "",
				smtp_port: response.data.smtp_port || 587,
				smtp_user: response.data.smtp_user || "",
				smtp_pass: response.data.smtp_pass_set ? "********" : "",
				smtp_from: response.data.smtp_from || "",
				email_enabled: response.data.email_enabled || false,
				notify_on_create: response.data.notify_on_create || false,
				notify_on_accept: response.data.notify_on_accept || false,
				notify_on_reject: response.data.notify_on_reject || false,
				notify_on_expiring: response.data.notify_on_expiring || false,
				admin_notification_email: response.data.admin_notification_email || "",
			});
		} catch (error) {
			console.error("Error fetching settings:", error);
			showNotification("Erro ao carregar configuracoes", "error");
		} finally {
			setLoading(false);
		}
	}, [showNotification]);

	const handleSave = async () => {
		try {
			setSaving(true);
			await client.post("/settings", settings);
			showNotification("Configuracoes salvas com sucesso!");
			if (onSettingsSaved) onSettingsSaved();
		} catch (error) {
			console.error("Error saving settings:", error);
			showNotification(
				error.response?.data?.error || "Erro ao salvar",
				"error",
			);
		} finally {
			setSaving(false);
		}
	};

	const handleTestWebhook = async () => {
		try {
			setTestingWebhook(true);
			await client.post("/settings/test-webhook");
			showNotification("Webhook testado com sucesso!");
		} catch (error) {
			console.error("Error testing webhook:", error);
			showNotification(
				error.response?.data?.error || "Erro ao testar webhook",
				"error",
			);
		} finally {
			setTestingWebhook(false);
		}
	};

	const handleTestEmail = async () => {
		try {
			setTestingEmail(true);
			await client.post("/settings/test-email");
			showNotification("Conexao SMTP verificada com sucesso!");
		} catch (error) {
			console.error("Error testing email:", error);
			showNotification(
				error.response?.data?.error || "Erro ao testar conexao SMTP",
				"error",
			);
		} finally {
			setTestingEmail(false);
		}
	};

	// Rejection Reasons CRUD
	const fetchRejectionReasons = useCallback(async () => {
		try {
			setReasonsLoading(true);
			const res = await client.get("/rejection-reasons");
			setRejectionReasons(res.data);
		} catch (error) {
			console.error("Error fetching rejection reasons:", error);
		} finally {
			setReasonsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchSettings();
		fetchRejectionReasons();
	}, [fetchSettings, fetchRejectionReasons]);

	const handleAddReason = async () => {
		if (!newReasonLabel.trim()) return;
		try {
			await client.post("/rejection-reasons", { label: newReasonLabel.trim() });
			setNewReasonLabel("");
			fetchRejectionReasons();
			showNotification("Motivo adicionado!");
		} catch (error) {
			showNotification(
				error.response?.data?.error || "Erro ao adicionar motivo",
				"error",
			);
		}
	};

	const handleUpdateReason = async (reason) => {
		try {
			await client.put(`/rejection-reasons/${reason.id}`, reason);
			fetchRejectionReasons();
		} catch (_error) {
			showNotification("Erro ao atualizar motivo", "error");
		}
	};

	const handleDeleteReason = async (id) => {
		if (!window.confirm("Tem certeza que deseja excluir este motivo?")) return;
		try {
			await client.delete(`/rejection-reasons/${id}`);
			fetchRejectionReasons();
			showNotification("Motivo excluido!");
		} catch (_error) {
			showNotification("Erro ao excluir motivo", "error");
		}
	};

	const handleMoveReason = async (idx, direction) => {
		const items = [...rejectionReasons];
		const targetIdx = idx + direction;
		if (targetIdx < 0 || targetIdx >= items.length) return;
		[items[idx], items[targetIdx]] = [items[targetIdx], items[idx]];
		const reordered = items.map((item, i) => ({
			id: item.id,
			sort_order: i + 1,
		}));
		try {
			await client.put("/rejection-reasons/reorder", { items: reordered });
			fetchRejectionReasons();
		} catch (_error) {
			showNotification("Erro ao reordenar", "error");
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center py-12">
				<p className="text-slate-300">Carregando...</p>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			<h1 className="text-2xl font-bold text-white">
				Configuracoes da Aplicacao
			</h1>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* App Settings */}
				<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 space-y-4">
					<div className="flex items-center gap-2 mb-2">
						<Globe className="w-5 h-5 text-cyan-400" />
						<h2 className="text-lg font-semibold text-white">Aparencia</h2>
					</div>

					<Input
						label="Nome da Aplicacao"
						value={settings.app_name}
						onChange={(e) =>
							setSettings({ ...settings, app_name: e.target.value })
						}
						placeholder="Proposta Pro"
					/>

					<div>
						<label
							htmlFor="app_logo_url"
							className="block text-sm font-medium text-slate-300 mb-2"
						>
							URL do Logo
						</label>
						<input
							id="app_logo_url"
							type="url"
							value={settings.app_logo_url}
							onChange={(e) =>
								setSettings({ ...settings, app_logo_url: e.target.value })
							}
							placeholder="https://..."
							className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 transition-all"
						/>
						{settings.app_logo_url && (
							<div className="mt-3 p-3 bg-slate-800/30 rounded border border-slate-700">
								<p className="text-xs text-slate-400 mb-2">Preview:</p>
								<img
									src={settings.app_logo_url}
									alt="Logo"
									className="h-12 object-contain"
								/>
							</div>
						)}
					</div>
				</div>

				{/* Webhook Settings */}
				<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 space-y-4">
					<div className="flex items-center gap-2 mb-2">
						<Send className="w-5 h-5 text-cyan-400" />
						<h2 className="text-lg font-semibold text-white">Webhooks</h2>
					</div>

					<TextArea
						label="URL do Webhook"
						value={settings.webhook_url}
						onChange={(e) =>
							setSettings({ ...settings, webhook_url: e.target.value })
						}
						placeholder="https://webhook.site/seu-uuid"
						rows="3"
					/>

					<Button
						onClick={handleTestWebhook}
						loading={testingWebhook}
						variant="secondary"
						className="w-full"
					>
						<Send className="w-4 h-4 inline mr-2" />
						Testar Webhook
					</Button>

					<div className="bg-blue-600/10 border border-blue-500/20 rounded-lg p-3 space-y-2">
						<p className="text-xs text-blue-300 font-semibold">
							Eventos de Webhook
						</p>
						<ul className="text-xs text-blue-200 space-y-1 ml-3">
							<li>- Proposta Aceita</li>
							<li>- Proposta Rejeitada</li>
							<li>- Teste de Webhook</li>
						</ul>
					</div>
				</div>
			</div>

			{/* SMTP / Email Settings */}
			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 space-y-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Mail className="w-5 h-5 text-cyan-400" />
						<h2 className="text-lg font-semibold text-white">
							Configuracao de Email (SMTP)
						</h2>
					</div>
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={settings.email_enabled}
							onChange={(e) =>
								setSettings({ ...settings, email_enabled: e.target.checked })
							}
							className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30"
						/>
						<span className="text-sm text-slate-300">
							Ativar envio de email
						</span>
					</label>
				</div>

				<div
					className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${!settings.email_enabled && "opacity-50 pointer-events-none"}`}
				>
					<Input
						label="Servidor SMTP"
						value={settings.smtp_host}
						onChange={(e) =>
							setSettings({ ...settings, smtp_host: e.target.value })
						}
						placeholder="smtp.gmail.com"
					/>
					<Input
						label="Porta SMTP"
						type="number"
						value={settings.smtp_port}
						onChange={(e) =>
							setSettings({
								...settings,
								smtp_port: parseInt(e.target.value, 10) || 587,
							})
						}
						placeholder="587"
					/>
					<Input
						label="Usuario SMTP"
						value={settings.smtp_user}
						onChange={(e) =>
							setSettings({ ...settings, smtp_user: e.target.value })
						}
						placeholder="usuario@dominio.com"
					/>
					<Input
						label="Senha SMTP"
						type="password"
						value={settings.smtp_pass}
						onChange={(e) =>
							setSettings({ ...settings, smtp_pass: e.target.value })
						}
						placeholder="Senha ou App Password"
					/>
					<Input
						label="Email de Envio (From)"
						value={settings.smtp_from}
						onChange={(e) =>
							setSettings({ ...settings, smtp_from: e.target.value })
						}
						placeholder="propostas@suaempresa.com"
					/>
					<div className="flex items-end">
						<Button
							onClick={handleTestEmail}
							loading={testingEmail}
							variant="secondary"
							className="w-full"
							disabled={!settings.email_enabled}
						>
							<Mail className="w-4 h-4 inline mr-2" />
							Testar Conexao SMTP
						</Button>
					</div>
				</div>

				{settings.email_enabled && (
					<div className="bg-blue-600/10 border border-blue-500/20 rounded-lg p-3">
						<p className="text-xs text-blue-300">
							Quando ativado, voce podera enviar propostas por email diretamente
							pela plataforma. Para Gmail, use uma "Senha de App" nas
							configuracoes de seguranca da conta.
						</p>
					</div>
				)}
			</div>

			{/* Notificacoes Automaticas */}
			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 space-y-4">
				<div className="flex items-center gap-2 mb-2">
					<Bell className="w-5 h-5 text-cyan-400" />
					<h2 className="text-lg font-semibold text-white">
						Notificacoes Automaticas
					</h2>
				</div>

				<Input
					label="Email do Administrador"
					value={settings.admin_notification_email}
					onChange={(e) =>
						setSettings({
							...settings,
							admin_notification_email: e.target.value,
						})
					}
					placeholder="admin@suaempresa.com"
				/>
				<p className="text-xs text-slate-400 -mt-2">
					Endereco de email onde as notificacoes automaticas serao enviadas.
				</p>

				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
					{[
						{ key: "notify_on_create", label: "Notificar ao criar proposta" },
						{ key: "notify_on_accept", label: "Notificar ao aceitar proposta" },
						{
							key: "notify_on_reject",
							label: "Notificar ao rejeitar proposta",
						},
						{
							key: "notify_on_expiring",
							label: "Notificar propostas expirando",
						},
					].map(({ key, label }) => (
						<label
							key={key}
							htmlFor={key}
							className="flex items-center gap-3 p-3 bg-slate-800/50 border border-white/5 rounded-lg cursor-pointer hover:border-cyan-500/30 transition-all group"
						>
							<div className="relative flex items-center">
								<input
									id={key}
									type="checkbox"
									checked={settings[key]}
									onChange={(e) =>
										setSettings({ ...settings, [key]: e.target.checked })
									}
									className="sr-only peer"
								/>
								<div className="w-9 h-5 bg-slate-700 rounded-full peer-checked:bg-cyan-500 transition-colors" />
								<div className="absolute left-0.5 top-0.5 w-4 h-4 bg-slate-300 rounded-full shadow peer-checked:translate-x-4 peer-checked:bg-white transition-transform" />
							</div>
							<span className="text-sm text-slate-300 group-hover:text-white transition-colors">
								{label}
							</span>
						</label>
					))}
				</div>

				<div className="bg-blue-600/10 border border-blue-500/20 rounded-lg p-3">
					<p className="text-xs text-blue-300">
						As notificacoes automaticas serao enviadas para o email do
						administrador usando as configuracoes SMTP definidas acima.
						Certifique-se de que o envio de email esteja ativado.
					</p>
				</div>
			</div>

			{/* Motivos de Rejeicao */}
			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 space-y-4">
				<div className="flex items-center gap-2 mb-2">
					<XCircle className="w-5 h-5 text-cyan-400" />
					<h2 className="text-lg font-semibold text-white">
						Motivos de Rejeicao
					</h2>
				</div>
				<p className="text-xs text-slate-400 -mt-2">
					Gerencie as opcoes de motivo que o cliente vera ao rejeitar uma
					proposta.
				</p>

				{/* Add new reason */}
				<div className="flex gap-2">
					<input
						type="text"
						value={newReasonLabel}
						onChange={(e) => setNewReasonLabel(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleAddReason()}
						placeholder="Novo motivo de rejeicao..."
						className="flex-1 bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 transition-all text-sm"
					/>
					<Button
						onClick={handleAddReason}
						variant="secondary"
						size="sm"
						disabled={!newReasonLabel.trim()}
					>
						<Plus className="w-4 h-4 mr-1" /> Adicionar
					</Button>
				</div>

				{/* Reasons list */}
				{reasonsLoading ? (
					<div className="flex items-center justify-center py-4">
						<Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
					</div>
				) : (
					<div className="space-y-2">
						{rejectionReasons.map((reason, idx) => (
							<div
								key={reason.id}
								className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
									reason.is_active
										? "bg-slate-800/50 border-white/5 hover:border-cyan-500/30"
										: "bg-slate-800/20 border-white/5 opacity-60"
								}`}
							>
								{/* Reorder arrows */}
								<div className="flex flex-col gap-0.5">
									<button
										type="button"
										onClick={() => handleMoveReason(idx, -1)}
										disabled={idx === 0}
										className="text-slate-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
									>
										<ChevronUp className="w-4 h-4" />
									</button>
									<button
										type="button"
										onClick={() => handleMoveReason(idx, 1)}
										disabled={idx === rejectionReasons.length - 1}
										className="text-slate-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
									>
										<ChevronDown className="w-4 h-4" />
									</button>
								</div>

								{/* Label (inline edit) */}
								<input
									type="text"
									value={reason.label}
									onChange={(e) => {
										const updated = rejectionReasons.map((r) =>
											r.id === reason.id ? { ...r, label: e.target.value } : r,
										);
										setRejectionReasons(updated);
									}}
									onBlur={() => handleUpdateReason(reason)}
									className="flex-1 bg-transparent border-none text-sm text-white focus:outline-none focus:bg-slate-700/30 rounded px-2 py-1 transition-all"
								/>

								{/* Active toggle */}
								<label
									htmlFor={`reason-active-${reason.id}`}
									className="flex items-center cursor-pointer"
									title={reason.is_active ? "Ativo" : "Inativo"}
								>
									<span className="sr-only">Ativo</span>
									<div className="relative flex items-center">
										<input
											id={`reason-active-${reason.id}`}
											type="checkbox"
											checked={reason.is_active}
											onChange={(e) =>
												handleUpdateReason({
													...reason,
													is_active: e.target.checked,
												})
											}
											className="sr-only peer"
										/>
										<div className="w-8 h-4 bg-slate-700 rounded-full peer-checked:bg-cyan-500 transition-colors" />
										<div className="absolute left-0.5 top-0.5 w-3 h-3 bg-slate-300 rounded-full shadow peer-checked:translate-x-4 peer-checked:bg-white transition-transform" />
									</div>
								</label>

								{/* Delete */}
								<button
									type="button"
									onClick={() => handleDeleteReason(reason.id)}
									className="text-slate-500 hover:text-red-400 transition-colors p-1"
									title="Excluir motivo"
								>
									<Trash2 className="w-4 h-4" />
								</button>
							</div>
						))}
						{rejectionReasons.length === 0 && (
							<p className="text-sm text-slate-500 text-center py-4">
								Nenhum motivo cadastrado
							</p>
						)}
					</div>
				)}
			</div>

			{/* Permissions Matrix */}
			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 space-y-4">
				<div className="flex items-center gap-2 mb-2">
					<Shield className="w-5 h-5 text-cyan-400" />
					<h2 className="text-lg font-semibold text-white">
						Permissoes por Role
					</h2>
				</div>
				<p className="text-xs text-slate-400 -mt-2">
					Matriz de permissoes de cada role no sistema. As roles sao fixas e nao
					podem ser editadas.
				</p>

				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-white/10">
								<th className="text-left px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
									Funcionalidade
								</th>
								{Object.entries(ROLES).map(([key, role]) => (
									<th key={key} className="text-center px-3 py-2">
										<span
											className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${role.badgeClass}`}
										>
											<role.icon className="w-2.5 h-2.5" />
											{role.label}
										</span>
									</th>
								))}
							</tr>
						</thead>
						<tbody className="divide-y divide-white/5">
							{PERMISSIONS_MATRIX.map((row, idx) => (
								<tr
									key={idx}
									className="hover:bg-white/[0.02] transition-colors"
								>
									<td className="px-3 py-2 text-xs text-slate-300">
										{row.feature}
									</td>
									{["admin", "manager", "salesperson", "viewer"].map((role) => (
										<td key={role} className="text-center px-3 py-2">
											{row[role] ? (
												<Check className="w-4 h-4 text-green-400 mx-auto" />
											) : (
												<X className="w-4 h-4 text-slate-600 mx-auto" />
											)}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{/* Save Button */}
			<div className="flex justify-end">
				<Button onClick={handleSave} loading={saving} size="lg">
					<CheckCircle2 className="w-5 h-5 inline mr-2" />
					Salvar Todas as Configuracoes
				</Button>
			</div>

			{/* Payload Info */}
			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
				<h2 className="text-lg font-semibold text-white mb-4">
					Estrutura do Payload (Webhook)
				</h2>

				<div className="bg-slate-800/30 rounded-lg p-4 overflow-x-auto">
					<pre className="text-xs text-slate-300 font-mono">
						{`{
  "event": "proposal.accepted" | "proposal.rejected",
  "proposal_id": 123,
  "proposal_number": "PROP-001",
  "client_id": 456,
  "client_name": "Joao Silva",
  "status": "ACCEPTED" | "REJECTED",
  "approver_name": "Joao Silva",
  "approver_email": "joao@empresa.com",
  "approver_role": "Gerente",
  "rejection_reason": "fora-orcamento",
  "timestamp": "2026-02-10T14:30:00Z"
}`}
					</pre>
				</div>
			</div>

			{/* Security Info */}
			<div className="bg-amber-600/10 border border-amber-500/20 rounded-xl p-6">
				<div className="flex items-center gap-2 mb-3">
					<Shield className="w-5 h-5 text-amber-400" />
					<p className="text-amber-300 font-semibold">Dicas de Seguranca</p>
				</div>
				<ul className="text-amber-200 text-sm space-y-1">
					<li>- Use HTTPS para webhooks em producao</li>
					<li>
						- Para Gmail, ative "Senhas de App" em Configuracoes de Seguranca
					</li>
					<li>- Implemente retry logic para falhas de webhook</li>
					<li>- Registre todos os eventos em logs</li>
				</ul>
			</div>
		</div>
	);
}

Settings.propTypes = {
	onSettingsSaved: PropTypes.func,
};
